import { useEffect, useState } from "react";
import styled, { css, withTheme } from "styled-components";

import { AugmentedRequestConfig } from "../../types/utils";
import { QuerySettings } from "../../types/reports";
import { format, getRoundDate, request } from "../../utils";
import useGraphConfigCollection, {
	GraphConfigCollection
} from "../../hooks/use-graph-config-collection";
import {
	Bar,
	ForkedBar,
	GraphBox,
	GraphRow,
	HorizontalBar,
	Pie,
	Table
} from "../../components/viz";
import { CellRuntime, Column } from "../../components/viz/graphs/configs/table";
import useGraphBox, { OptionalGraphBox } from "../../hooks/use-graph-box";
import { DataPoint } from "../../components/viz/apply-categorization";
import { propsAndQueryParamsProxy } from "../../utils/standalone-utils";
import { MEDIA_TYPES } from "../../data/constants";
import { DOWNLOAD_PDF_DIV_ID } from "../../utils/download-pdf";
import { Race } from "../../state/features/data";

interface GraphsProps {
	querySettings: QuerySettings;
	collection: GraphConfigCollection;
	theme: any;
	raceName?: string;
	onSpendByPartyChange: (spend: any) => void;
	rollUp?: boolean;
	raceIdToName?: Record<any, string>;
}

const media = ["radio", "tv", "cable", "addressable"];

const monthNames = [
	"Jan",
	"Feb",
	"March",
	"April",
	"May",
	"June",
	"July",
	"Aug",
	"Sept",
	"Oct",
	"Nov",
	"Dec"
];

const DateTitle = styled.div`
	margin-top: 1em;
`;

const FullHeightGraphBox = styled(GraphBox)`
	min-height: 330px;
`;

const RaceTitle = styled.div`
	font-size: 2em;
	font-weight: bold;
	white-space: pre-line;
`;

const SnapshotTitle = styled.div`
	color: dimgray;
	font-size: 1em;
	font-weight: bold;
	text-transform: uppercase;
`;

const MissingInfo = styled.div`
	font-size: 1.5em;
	font-weight: bold;
	text-align: center;
`;

const TableGraphBox = styled(GraphBox)``;

const Title = styled.div`
	margin: 2em 0;
	position: relative;
	text-align: center;
	width: 100%;
`;

const titleStyle = css`
	font-size: 14px;
	color: #186a9f;
	background-color: white;
	text-transform: uppercase;
	font-weight: normal;
`;

interface SummaryGraphsProps extends GraphsProps {
	mkRequest: (
		url: string,
		raceId: any,
		mode: "teams" | "campaigns",
		process?: (data: any) => any
	) => AugmentedRequestConfig;
	raceId: any;
	raceName?: string;
}

function SummaryGraphs({
	querySettings,
	collection,
	raceId,
	raceName,
	mkRequest,
	theme
}: SummaryGraphsProps) {
	const processSOVData = (sov: any) => {
		const campaigns = [];

		for (const k in sov) {
			if (!sov.hasOwnProperty(k)) continue;

			const candidates = sov[k];

			for (const candidate of candidates) {
				const entry = {
					name: candidate.candidate_name,
					tv: 0,
					radio: 0,
					cable: 0,
					addressable: 0,
					grp: 0,
					total: 0
				} as any;

				for (const medium of media) {
					const d = candidate[medium] || {
						grp: 0,
						usd: 0
					};

					entry[medium] = d.usd;
					entry.total += d.usd;
					entry.grp += d.grp;
				}

				if (entry.total <= 0) continue;

				campaigns.push(entry);
			}
		}

		return campaigns;
	};

	const processSOVDataByMarket = (sov: any) => {
		const markets = [];

		for (const market in sov) {
			if (!sov.hasOwnProperty(market)) continue;

			const campaigns = processSOVData(sov[market]);
			markets.push({ market, campaigns });
		}

		return markets;
	};

	const processAddressableData = (sov: any) => {
		const addressable = [];
		for (const name in sov) {
			if (!sov.hasOwnProperty(name)) continue;
			const campaigns = sov[name];
			addressable.push({
				name:
					MEDIA_TYPES.find(
						medium => medium.value === name
					)?.label || name,
				campaigns
			});
		}
		return addressable;
	};

	const filterColumns = (columns: Column[]): Column[] => {
		const mediaMap = media.reduce((map, entry) => {
			map[entry.toLowerCase()] = true;
			return map;
		}, {} as any);

		return columns.filter(column => {
			return !(
				(column.accessor === "tv" ||
					column.accessor === "cable" ||
					column.accessor === "radio" ||
					column.accessor === "addressable") &&
				!mediaMap.hasOwnProperty(column.accessor)
			);
		});
	};

	const displayUsd = (rt: CellRuntime) => format.wholeDollar(rt.value);
	const displayNumber = (rt: CellRuntime) => format.number(rt.value);
	const displayHeader = (rt: CellRuntime) => {
		if (rt.index === 0) {
			return rt.print(rt.section.group!.label).weight(700);
		}

		if (rt.index < rt.columns.length - 1) return rt.print(rt.value);

		const total = rt.section.rows.reduce((tot, row) => {
			return tot + row.cells[rt.index].value;
		}, 0);

		return rt.display(total).weight(700);
	};

	const dateRange = querySettings.dateRange
		.map(d =>
			new Date(d).toLocaleDateString("en-US", {
				timeZone: "UTC"
			})
		)
		.join(" - ");

	const overview = useGraphBox({
		graph: Table,
		title: `Snapshot\u00a0\u00a0(${raceName})\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0${dateRange}`,
		request: mkRequest(
			"/spending/sov/media",
			raceId,
			"campaigns",
			d => {
				return processSOVData(d);
			}
		),
		// request: getTableData,
		bare: true,
		box: TableGraphBox,
		layout: {
			legends: null
		},
		titleStyle: titleStyle,
		store: {
			display: displayUsd,
			columns: filterColumns([
				{ title: "", accessor: "name", display: null },
				{ title: "TV", accessor: "tv", align: "end" },
				{
					title: "GRP",
					accessor: "grp",
					align: "end",
					display: displayNumber
				},
				{
					title: "Cable",
					accessor: "cable",
					align: "end"
				},
				{
					title: "Radio",
					accessor: "radio",
					align: "end"
				},
				{
					title: "Addr.",
					accessor: "addressable",
					align: "end"
				},
				{
					title: "Total",
					accessor: "total",
					align: "end"
				}
			]),
			fontSize: 14,
			rowBorders: true,
			themeColor: "#80a4c6"
		},
		getHeight: d => (d.length + 1) * 25,
		track: collection.track(`${raceId}-top-spenders`)
	});

	const marketSpend = useGraphBox({
		graph: Table,
		title: "",
		request: mkRequest(
			"/spending/sov/market",
			raceId,
			"campaigns",
			d => {
				return processSOVDataByMarket(d);
			}
		),
		bare: true,
		box: TableGraphBox,
		layout: {
			legends: null,
			labelKey: "market"
		},
		store: {
			header: displayHeader,
			display: displayUsd,
			columns: filterColumns([
				{
					title: "Campaign",
					accessor: "name",
					display: null
				},
				{ title: "TV", accessor: "tv", align: "end" },
				{
					title: "GRP",
					accessor: "grp",
					align: "end",
					display: displayNumber
				},
				{
					title: "Cable",
					accessor: "cable",
					align: "end"
				},
				{
					title: "Radio",
					accessor: "radio",
					align: "end"
				},
				{
					title: "Total",
					accessor: "total",
					align: "end"
				}
			]),
			themeColor: theme.graphBackground
		},
		getHeight: d => {
			const rows = d.reduce((tot: number, dt: any) => {
				return tot + 1 + dt.campaigns.length;
			}, 0);

			return rows * 25;
		},
		track: collection.track(`${raceId}-market-spend`)
	});

	const addressableSpend = useGraphBox({
		graph: Table,
		title: "",
		request: mkRequest(
			"/spending/sov/addressable",
			raceId,
			"campaigns",
			d => {
				return processAddressableData(d);
			}
		),
		bare: true,
		box: TableGraphBox,
		layout: {
			legends: null,
			labelKey: "name"
		},
		store: {
			header: displayHeader,
			display: displayUsd,
			columns: filterColumns([
				{
					title: "Campaign",
					accessor: "candidate_name",
					display: null
				},
				{
					title: "Total",
					accessor: "total",
					align: "start"
				}
			]),
			themeColor: theme.graphBackground
		},
		getHeight: d => {
			const rows = d.reduce((tot: number, dt: any) => {
				return tot + 1 + dt.campaigns.length;
			}, 0);

			return rows * 25;
		},
		track: collection.track(`${raceId}-addressable-spend`)
	});

	return (
		<>
			<GraphRow breakBefore="page">{overview}</GraphRow>
			<GraphRow breakBefore="page">{marketSpend}</GraphRow>
			<GraphRow>{addressableSpend}</GraphRow>
		</>
	);
}

const Graphs = (props: GraphsProps) => {
	const { selections } = props.querySettings.selectionPayload!,
		collection = props.collection,
		colors = props.theme.graphs.palettes.colors,
		partyColors = props.theme.graphs.palettes.parties
	const teamColors = selections.teams?.length
		? selections.teams.reduce((acc, team: any, i) => ({
			...acc,
			[team.full_name]: team.color || colors[i % colors.length]
		}), {})
		: {}
	const raceIds = selections["raceId"] || selections["raceIds"];

	const [spendByParty, setSpendByParty] = useState();

	useEffect(() => {
		if (spendByParty) {
			props.onSpendByPartyChange(spendByParty);
		}
	}, [spendByParty]);

	const statesQuery = selections.state
		? { states: [selections.state] }
		: {};

	const query = {
		media,
		range: props.querySettings.normalizedDateRange
			.map(ts => new Date(ts).toISOString())
			.join(),
		...statesQuery
	};

	const rollUp = props.rollUp || false;

	const ommittedGraphs = rollUp
		? [
			"team-spending-party",
			"team-usd-market",
			"team-sov-usd",
			"spending-party",
			"usd-market",
			"sov-usd"
		]
		: [];

	const mkRequest = (
		url: string,
		mode: "teams" | "campaigns",
		process?: (data: any) => any
	): AugmentedRequestConfig => {
		const updatedQuery = { ...query } as any;
		if (mode === "teams" && selections.teams) {
			updatedQuery.teams = selections.teams.map(
				(t: any) => t.id
			);
		}
		if (selections.campaigns) {
			updatedQuery.campaigns = selections.campaigns;
		}
		if (raceIds) {
			updatedQuery.race_ids = raceIds;
		}
		return {
			url: `/races/graphs${url}`,
			query: {
				...updatedQuery
			},
			cacheHash: props.querySettings.hash,
			process
		};
	};

	const mkRequestRace = (
		url: string,
		raceId: any,
		mode: "teams" | "campaigns",
		process?: (data: any) => any
	): AugmentedRequestConfig => {
		const updatedQuery = { ...query } as any;
		if (mode === "teams" && selections.teams) {
			updatedQuery.teams = selections.teams.map(
				(t: any) => t.id
			);
		}
		if (selections.campaigns) {
			updatedQuery.campaigns = selections.campaigns;
		}
		updatedQuery.race_ids = [raceId];
		return {
			url: `/races/graphs${url}`,
			query: {
				...updatedQuery
			},
			cacheHash: props.querySettings.hash,
			process
		};
	};

	const teamsMediaSpend = !ommittedGraphs.includes(
		"team-spending-medium"
	) && (
			<OptionalGraphBox
				graph={Bar}
				title="Spend by Team"
				request={mkRequest("/spending/medium", "teams")}
				box={FullHeightGraphBox}
				tooltips={d =>
					`${d.label}: ${format.dollar(
						d.value as number
					)}`
				}
				groupLabel={(d: DataPoint[]) =>
					format.dollar(
						d.reduce(
							(acc, data) =>
								acc +
								(data.value as number),
							0
						)
					)
				}
				layout={{
					colors,
					colorIndices: [
						"cable",
						"radio",
						"tv",
						"addressable"
					]
				}}
				track={collection.track("team-spending-medium")}
			/>
		);

	const teamMediaTypeSpend = !ommittedGraphs.includes(
		"team-spending-party"
	) && (
			<OptionalGraphBox
				graph={ForkedBar}
				title="Media Type Spend by Team"
				request={mkRequest("/spending/party", "teams", data => {
					Object.keys(data).forEach(party => {
						data[party].forEach(
							(media: any, i: number) => {
								if (
									media.hasOwnProperty(
										"addressable"
									)
								) {
									// "addressable" is too long in table - change to "addr"
									media["addr"] =
										media[
										"addressable"
										];
									delete media[
										"addressable"
									];
								}
							}
						);
					});
					return data;
				})}
				box={FullHeightGraphBox}
				tooltips={d =>
					`${d.trace[d.trace.length - 1].label} (${d.trace[1].label
					}): ${format.dollar(d.value as number)}`
				}
				groupLabel={(d: DataPoint[]) =>
					format.dollar(
						d.reduce(
							(acc, data) =>
								acc +
								(data.value as number),
							0
						)
					)
				}
				layout={{
					labelKey: "candidate_name",
					order: [2, 0, 1],
					colors: teamColors
				}}
				track={collection.track("team-spending-party")}
			/>
		);

	const teamUsdMarket = !ommittedGraphs.includes("team-usd-market") && (
		<OptionalGraphBox
			graph={HorizontalBar}
			title="USD by Market"
			request={mkRequest("/spending/sov/filtered", "teams")}
			box={FullHeightGraphBox}
			tooltips={d =>
				`${d.label}: ${format.dollar(
					d.value as number
				)}`
			}
			groupLabel={(d: DataPoint[]) =>
				format.dollar(
					d.reduce(
						(acc, data) =>
							acc +
							(data.value as number),
						0
					)
				)
			}
			labelAllPoints={true}
			layout={{
				labelKey: "candidate",
				valueKey: "usd",
				colors: teamColors,
				colorRule: {
					track: [1, 2]
				}
			}}
			track={collection.track("team-usd-market")}
		/>
	);

	const teamsSov = !ommittedGraphs.includes("team-sov-usd") && (
		<OptionalGraphBox
			title="Share of Voice by Team"
			request={mkRequest("/spending/sov", "teams")}
			box={FullHeightGraphBox}
			graphs={[
				{
					component: Pie,
					subheading: "GRPs",
					tooltips: d =>
						`${d.trace[1].label} (${d.trace[0].label
						}): ${format.number(
							d.value as number
						)}`,
					layout: {
						labelKey: "candidate_name",
						valueKey: "grp",
						colors: teamColors,
						uniquePoints: true
					},
					track: collection.track("team-sov-grp")
				},
				{
					component: Pie,
					subheading: "USD",
					tooltips: d =>
						`${d.trace[1].label}, ${d.label
						} (${d.trace[0].label
						}): ${format.dollar(
							d.value as number
						)}`,
					layout: {
						legends: {
							level: 1
						},
						labelKey: "candidate_name",
						level: 1,
						shortValueKey: "usd",
						colors: teamColors,
						uniquePoints: true
					},
					track: collection.track("team-sov-usd")
				}
			]}
		/>
	);

	const mediaSpend = !ommittedGraphs.includes("spending-medium") && (
		<OptionalGraphBox
			graph={Bar}
			title="Spend by Campaign"
			request={mkRequest("/spending/medium", "campaigns")}
			box={FullHeightGraphBox}
			tooltips={d =>
				`${d.label}: ${format.dollar(
					d.value as number
				)}`
			}
			groupLabel={(d: DataPoint[]) =>
				format.dollar(
					d.reduce(
						(acc, data) =>
							acc +
							(data.value as number),
						0
					)
				)
			}
			layout={{
				colors,
				colorIndices: [
					"cable",
					"radio",
					"tv",
					"addressable"
				]
			}}
			track={collection.track("spending-medium")}
		/>
	);

	const partySpend = !ommittedGraphs.includes("spending-party") && (
		<OptionalGraphBox
			graph={ForkedBar}
			title="Media Type Spend by Campaign"
			request={mkRequest(
				"/spending/party",
				"campaigns",
				data => {
					const aggByParty: any = {};
					Object.keys(data).forEach(party => {
						aggByParty[party] = 0;
						data[party].forEach(
							(
								media: any,
								i: number
							) => {
								if (
									media.hasOwnProperty(
										"addressable"
									)
								) {
									// "addressable" is too long in table - change to "addr"
									media[
										"addr"
									] =
										media[
										"addressable"
										];
									delete media[
										"addressable"
									];
								}
								Object.values(
									media
								).forEach(
									(
										v: any
									) => {
										if (
											!isNaN(
												v
											)
										) {
											aggByParty[
												party
											] +=
												v;
										}
									}
								);
							}
						);
					});
					setSpendByParty(aggByParty);
					return data;
				}
			)}
			box={FullHeightGraphBox}
			tooltips={d =>
				`${d.trace[d.trace.length - 1].label} (${d.trace[1].label
				}): ${format.dollar(d.value as number)}`
			}
			groupLabel={(d: DataPoint[]) =>
				format.dollar(
					d.reduce(
						(acc, data) =>
							acc +
							(data.value as number),
						0
					)
				)
			}
			layout={{
				labelKey: "candidate_name",
				order: [2, 0, 1],
				colors: partyColors
			}}
			track={collection.track("spending-party")}
		/>
	);

	const usdMarket = !ommittedGraphs.includes("usd-market") && (
		<OptionalGraphBox
			graph={HorizontalBar}
			title="USD by Market"
			request={mkRequest(
				"/spending/sov/filtered",
				"campaigns"
			)}
			box={FullHeightGraphBox}
			tooltips={d =>
				`${d.label}: ${format.dollar(
					d.value as number
				)}`
			}
			groupLabel={(d: DataPoint[]) =>
				format.dollar(
					d.reduce(
						(acc, data) =>
							acc +
							(data.value as number),
						0
					)
				)
			}
			labelAllPoints={true}
			layout={{
				labelKey: "candidate",
				valueKey: "usd",
				colors: partyColors,
				colorRule: {
					track: [1, 2]
				}
			}}
			track={collection.track("usd-market")}
		/>
	);

	const campaignsSov = !ommittedGraphs.includes("sov-usd") && (
		<OptionalGraphBox
			title="Share of Voice by Campaign"
			request={mkRequest("/spending/sov", "campaigns")}
			box={FullHeightGraphBox}
			graphs={[
				{
					component: Pie,
					subheading: "GRPs",
					tooltips: d =>
						`${d.trace[1].label} (${d.trace[0].label
						}): ${format.number(
							d.value as number
						)}`,
					layout: {
						labelKey: "candidate_name",
						valueKey: "grp",
						colors: partyColors,
						uniquePoints: true
					},
					track: collection.track("sov-grp")
				},
				{
					component: Pie,
					subheading: "USD",
					tooltips: d =>
						`${d.trace[1].label}, ${d.label
						} (${d.trace[0].label
						}): ${format.dollar(
							d.value as number
						)}`,
					layout: {
						legends: {
							level: 1
						},
						labelKey: "candidate_name",
						level: 1,
						shortValueKey: "usd",
						colors: partyColors,
						uniquePoints: true
					},
					track: collection.track("sov-usd")
				}
			]}
		/>
	);

	return (
		<>
			{selections.teams.length > 0 && (
				<>
					{teamsMediaSpend && (
						<GraphRow>
							{teamsMediaSpend}
						</GraphRow>
					)}
					{teamMediaTypeSpend && (
						<GraphRow>
							{teamMediaTypeSpend}
						</GraphRow>
					)}
					{teamUsdMarket && (
						<GraphRow>
							{teamUsdMarket}
						</GraphRow>
					)}
					{teamsSov && (
						<GraphRow>{teamsSov}</GraphRow>
					)}
				</>
			)}
			{mediaSpend && <GraphRow>{mediaSpend}</GraphRow>}
			{partySpend && <GraphRow>{partySpend}</GraphRow>}
			{usdMarket && <GraphRow>{usdMarket}</GraphRow>}
			{campaignsSov && <GraphRow>{campaignsSov}</GraphRow>}
			{raceIds.map((raceId: any) => (
				<SummaryGraphs
					key={raceId}
					raceId={raceId}
					mkRequest={mkRequestRace}
					querySettings={props.querySettings}
					collection={collection}
					theme={props.theme}
					onSpendByPartyChange={
						props.onSpendByPartyChange
					}
					raceName={props.raceIdToName?.[raceId]}
				/>
			))}
		</>
	);
};

function parseDate(dateString: string | null) {
	if (dateString) {
		const millis = parseInt(dateString);
		if (!isNaN(millis) && isFinite(millis)) {
			return new Date(millis);
		}
	}
	return null;
}

export function getDateRange(params: any) {
	const startDateFromQuery: Date | null = parseDate(params.start_date);
	const endDateFromQuery: Date | null = parseDate(params.end_date);

	if (startDateFromQuery && endDateFromQuery) {
		return [
			startDateFromQuery.getTime(),
			endDateFromQuery.getTime()
		] as [number, number];
	}
	const startDate = getRoundDate(Date.now());
	const endDate = getRoundDate(startDate, 7);
	return [startDate.getTime(), endDate.getTime()] as [number, number];
}

const AgencyLogo = styled.img`
	object-fit: contain;
	position: absolute;
	height: 77.5px;
	top: 0;
	left: 0;
`;

function getRaceIdsFromParams(raceIdParams: any) {
	if (!raceIdParams) return [];
	if (Array.isArray(raceIdParams)) {
		return raceIdParams.filter(Boolean);
	}
	if (typeof raceIdParams === "string" && raceIdParams.includes(",")) {
		return raceIdParams.split(",").filter(Boolean);
	}
	return [raceIdParams];
}

const Content = withTheme((props: any) => {
	const params = propsAndQueryParamsProxy(props);
	const [raceName, setRaceName] = useState<string>("");
	const [raceShortName, setRaceShortName] = useState<string>("");
	const [raceIdToName, setRaceIdToName] = useState<Record<any, string>>(
		{}
	);
	const [agencyLogo, setAgencyLogo] = useState<string>("");

	const dateRange = getDateRange(params);
	const raceIds = getRaceIdsFromParams(params.race_id || params.race_ids);

	const state: any = params.state;
	const agencyId: any = params.agency_id;

	const [teams, setTeams] = useState<any[]>([]);
	const [spendByParty, setSpendByParty] = useState();

	const rollUpSnapshot = params.rollup_mode === "rollup";

	useEffect(() => {
		let isCancelled = false;

		if (raceIds.length) {
			const raceRequests = raceIds.map(id =>
				request({ url: `/races/${id}` })
			);
			const teamsRequests = raceIds.map(id =>
				request({
					url: "/teams/list",
					query: { race_id: id }
				})
			);

			const colors = props.theme.graphs.palettes.colors;

			Promise.all([...raceRequests, ...teamsRequests]).then(
				responses => {
					if (isCancelled) return;
					const raceData = responses.slice(
						0,
						raceIds.length
					);
					const teamsData = responses.slice(
						raceIds.length
					);

					const validRaceData = raceData
						.filter(
							r =>
								r?.data &&
								r.success
						)
						.map(r => r.data as Race);
					const newTeams = teamsData.flatMap(r =>
						r?.data && r.success
							? r.data.map((t: any, i: number) => ({ ...t, color: t.color || colors[i % colors.length] }))
							: []
					);

					setRaceIdToName(
						validRaceData.reduce(
							(acc, r) => {
								acc[r.id] =
									r.race_name;
								return acc;
							},
							{} as Record<
								any,
								string
							>
						)
					);

					const firstRace = validRaceData[0];
					if (
						firstRace &&
						validRaceData.length > 1 &&
						validRaceData.every(
							r =>
								r.office.id ===
								firstRace.office
									.id
						)
					) {
						const state =
							validRaceData.every(
								r =>
									r.jurisdiction ===
									firstRace.jurisdiction
							)
								? `${firstRace.jurisdiction} - `
								: "";
						setRaceName(
							`${state}${firstRace.office.full_name} ${firstRace.year}`
						);
						setRaceShortName(
							`${state}${firstRace.office.short_name} ${firstRace.year}`
						);
					} else {
						setRaceName(
							validRaceData
								.map(
									r =>
										`${r.race_name} ${r.year}`
								)
								.join("\n")
						);
						setRaceShortName(
							validRaceData
								.map(
									r =>
										r.race_name
								)
								.join(", ")
						);
					}

					setTeams(prev =>
						JSON.stringify(prev) ===
							JSON.stringify(newTeams)
							? prev
							: newTeams
					);
				}
			);
		}

		return () => {
			isCancelled = true;
		};
	}, [JSON.stringify(raceIds)]);

	useEffect(() => {
		if (!agencyId) {
			setAgencyLogo("");
			return;
		}
		request({
			url: `/agencies/${agencyId}`
		}).then(response => {
			if (!response.success) return;
			if (response.data.logo) {
				setAgencyLogo(response.data.logo);
			}
		});
	}, [agencyId]);

	const collection = useGraphConfigCollection();

	const startDate = new Date(dateRange[0]);
	const endDate = new Date(dateRange[1]);

	const renderMissingInfo = () => {
		if (!raceIds) {
			return <MissingInfo>No race ID set</MissingInfo>;
		}
	};

	const stateString = state ? ` (${state})` : "";

	function renderAgencyLogo() {
		if (!agencyLogo) return undefined;
		return <AgencyLogo src={agencyLogo} />;
	}

	function renderSpendByParty() {
		if (!spendByParty || !Object.keys(spendByParty).length) {
			return undefined;
		}

		const partyColors = props.theme.graphs.palettes.parties;

		const total = Object.keys(spendByParty).reduce(
			(totalSpend, nextParty) =>
				totalSpend + spendByParty[nextParty],
			0
		);
		return (
			<table
				style={{
					position: "absolute",
					right: "1rem",
					top: 0
				}}
			>
				<tbody>
					{Object.keys(spendByParty).map(
						party => {
							const color =
								partyColors[
								party
								] ||
								partyColors[
								"other"
								] ||
								"black";
							return (
								<tr key={party}>
									<th
										style={{
											color,
											textAlign: "right"
										}}
									>
										<strong>
											{
												party
											}
										</strong>
									</th>
									<td
										style={{
											textAlign: "right"
										}}
									>
										{format.wholeDollar(
											spendByParty[
											party
											]
										)}
									</td>
								</tr>
							);
						}
					)}
				</tbody>
				<tfoot>
					<tr>
						<th
							style={{
								textAlign: "right"
							}}
						>
							<strong>Total</strong>
						</th>
						<td
							style={{
								textAlign: "right"
							}}
						>
							{format.wholeDollar(
								total
							)}
						</td>
					</tr>
				</tfoot>
			</table>
		);
	}

	return (
		<div style={{ overflow: "auto" }} id={DOWNLOAD_PDF_DIV_ID}>
			<Title>
				<SnapshotTitle>Daily Snapshot</SnapshotTitle>
				<RaceTitle>
					{raceName}
					{stateString}
				</RaceTitle>
				<DateTitle>
					{`${monthNames[
						startDate.getUTCMonth()
					]
						} ${startDate.getUTCDate()} - ${monthNames[
						endDate.getUTCMonth()
						]
						} ${endDate.getUTCDate()} ${endDate.getUTCFullYear()}`}
				</DateTitle>
				{renderSpendByParty()}
				{renderAgencyLogo()}
			</Title>

			{raceIds && (
				<Graphs
					querySettings={{
						dateRange,
						hash: 0,
						normalizedDateRange: dateRange,
						selectionPayload: {
							errorMessage: "",
							errors: [],
							selection: [],
							selections: {
								campaigns: params.campaigns,
								raceIds,
								teams,
								state
							},
							valid: true
						}
					}}
					collection={collection}
					raceName={raceShortName}
					onSpendByPartyChange={setSpendByParty}
					theme={props.theme}
					rollUp={rollUpSnapshot}
					raceIdToName={raceIdToName}
				/>
			)}
			{!raceIds && renderMissingInfo()}
		</div>
	);
});

const DailySnapshot = (props: any) => {
	return <Content {...props} />;
};

export default DailySnapshot;
